// frontend/src/pages/EditProjectPage.jsx
// Edit an existing project's title, invention description, and mindblower context.

import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getProject, updateProject } from '../utils/api';
import { DarkModeContext } from '../App';

export default function EditProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [inventionTitle, setInventionTitle] = useState('');
  const [inventionDescription, setInventionDescription] = useState('');
  const [mindblowerContext, setMindblowerContext] = useState('');
  const [projectMode, setProjectMode] = useState('freeform');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await getProject(projectId);
        if (!alive) return;
        setInventionTitle(p.inventionTitle || '');
        setInventionDescription(p.inventionDescription || '');
        setMindblowerContext(p.mindblowerContext || '');
        setProjectMode(p.projectMode || 'freeform');
      } catch (err) {
        setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!inventionTitle.trim() || !inventionDescription.trim()) {
      setError('Title and description are required.');
      return;
    }
    try {
      setSaving(true);
      await updateProject(projectId, {
        inventionTitle: inventionTitle.trim(),
        inventionDescription,
        mindblowerContext: mindblowerContext || null
      });
      setSuccess('Saved.');
      setTimeout(() => navigate('/dashboard'), 600);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-700 dark:text-gray-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <Link to="/dashboard" className="text-xl font-bold text-gray-900 dark:text-gray-100 self-center">Patent Council</Link>
            <button
              onClick={toggleDarkMode}
              className="self-center p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
              aria-label="Toggle dark mode"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4 inline-block">← Back to dashboard</Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Edit Project</h1>
        {projectMode === 'seeded' && (
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            This is a seeded project. Editing title/description here does <strong>not</strong> re-parse the seed or re-run normalization. To replace the seed entirely, delete the project and create a new one.
          </p>
        )}

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-5">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invention Title</label>
            <input
              id="title"
              type="text"
              required
              value={inventionTitle}
              onChange={(e) => setInventionTitle(e.target.value)}
              className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{inventionTitle.length.toLocaleString()} characters</p>
          </div>

          <div>
            <label htmlFor="desc" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invention Description</label>
            <textarea
              id="desc"
              rows={12}
              required
              value={inventionDescription}
              onChange={(e) => setInventionDescription(e.target.value)}
              className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{inventionDescription.length.toLocaleString()} characters</p>
          </div>

          <div>
            <label htmlFor="mind" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mindblower Private Context</label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 mb-2">Visible only to the Mindblower agent.</p>
            <textarea
              id="mind"
              rows={5}
              value={mindblowerContext}
              onChange={(e) => setMindblowerContext(e.target.value)}
              className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{mindblowerContext.length.toLocaleString()} characters</p>
          </div>

          {error && <div role="alert" className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">{error}</div>}
          {success && <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-700 dark:text-green-400">{success}</div>}

          <div className="flex justify-end gap-3">
            <Link to="/dashboard" className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md text-sm">Cancel</Link>
            <button type="submit" disabled={saving} className="bg-blue-600 disabled:bg-gray-400 text-white hover:bg-blue-700 px-5 py-2 rounded-md text-sm font-medium">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
